import React, { useRef, useState } from "react";
import ResultImage from "./ResultImage";

const pad = (value) => String(value).padStart(2, "0");

const formatFilenameDate = (value) => {
    const date = value ? new Date(value) : new Date();

    if (Number.isNaN(date.getTime())) {
        return "unknown-date";
    }

    return [
        date.getFullYear(),
        pad(date.getMonth() + 1),
        pad(date.getDate()),
    ].join("-");
};

export default function ResultRenderer({ lotteryType, item, platformKey }) {
    const imageRef = useRef(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isCopying, setIsCopying] = useState(false);

    const freezeComputedStyles = (sourceNode, cloneNode) => {
        if (!(sourceNode instanceof Element) || !(cloneNode instanceof Element)) {
            return;
        }

        const computed = window.getComputedStyle(sourceNode);

        Array.from(computed).forEach((property) => {
            cloneNode.style.setProperty(
                property,
                computed.getPropertyValue(property),
                computed.getPropertyPriority(property)
            );
        });

        const sourceChildren = Array.from(sourceNode.children);
        const cloneChildren = Array.from(cloneNode.children);

        sourceChildren.forEach((child, index) => {
            freezeComputedStyles(child, cloneChildren[index]);
        });
    };

    const createExportClone = (target) => {
        const rect = target.getBoundingClientRect();
        const clone = target.cloneNode(true);
        const host = document.createElement("div");

        host.style.position = "fixed";
        host.style.left = "-10000px";
        host.style.top = "0";
        host.style.width = `${rect.width}px`;
        host.style.height = `${rect.height}px`;
        host.style.pointerEvents = "none";
        host.style.opacity = "1";
        host.style.zIndex = "-1";
        host.style.overflow = "hidden";

        clone.style.width = `${rect.width}px`;
        clone.style.height = `${rect.height}px`;
        clone.style.maxWidth = "none";
        clone.style.aspectRatio = "auto";
        clone.style.containerType = "inline-size";
        clone.style.contain = "none";

        freezeComputedStyles(target, clone);

        host.appendChild(clone);
        document.body.appendChild(host);

        return {
            host,
            clone,
            width: Math.round(rect.width),
            height: Math.round(rect.height),
        };
    };

    const renderResultCanvas = async () => {
        if (!imageRef.current) return null;

        const { default: html2canvas } = await import("html2canvas");

        if (document.fonts?.ready) {
            await document.fonts.ready;
        }

        const target = imageRef.current.querySelector(".result-image");
        if (!target) return null;

        const exportClone = createExportClone(target);

        try {
            return await html2canvas(exportClone.clone, {
                backgroundColor: null,
                scale: Math.max(window.devicePixelRatio || 1, 2),
                useCORS: true,
                width: exportClone.width,
                height: exportClone.height,
            });
        } finally {
            exportClone.host.remove();
        }
    };

    const getResultFilename = () =>
        `${String(lotteryType || "result").toLowerCase()}-result-${formatFilenameDate(
            item?.drawAt
        )}-${item?.drawingId || item?.id || "drawing"}.png`;

    const canvasToBlob = (canvas) =>
        new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error("Could not create PNG image"));
                }
            }, "image/png");
        });

    const handleDownload = async () => {
        if (!imageRef.current || isDownloading || isCopying) return;

        setIsDownloading(true);

        try {
            const canvas = await renderResultCanvas();
            if (!canvas) return;

            const link = document.createElement("a");
            link.href = canvas.toDataURL("image/png");
            link.download = getResultFilename();
            link.click();
        } finally {
            setIsDownloading(false);
        }
    };

    const handleCopyImage = async () => {
        if (
            !imageRef.current ||
            isDownloading ||
            isCopying ||
            !navigator.clipboard?.write ||
            !window.ClipboardItem
        ) {
            return;
        }

        setIsCopying(true);

        try {
            const blobPromise = renderResultCanvas().then((canvas) => {
                if (!canvas) {
                    throw new Error("Could not find result image");
                }

                return canvasToBlob(canvas);
            });

            await navigator.clipboard.write([
                new window.ClipboardItem({ "image/png": blobPromise }),
            ]);
        } catch (error) {
            console.error("Failed to copy result image", error);
        } finally {
            setIsCopying(false);
        }
    };

    const isExporting = isDownloading || isCopying;
    const canCopyImage = Boolean(navigator.clipboard?.write && window.ClipboardItem);

    return (
        <div className="result-card">
            <div ref={imageRef} className="result-card-image">
                <ResultImage drawing={item} lotteryType={lotteryType} platformKey={platformKey} />

                <button
                    type="button"
                    className="result-action-button result-download-button"
                    onClick={handleDownload}
                    disabled={isExporting}
                    aria-label={isDownloading ? "Generating PNG" : "Download PNG"}
                    title={isDownloading ? "Generating PNG" : "Download PNG"}
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            d="M12 4V14M12 14L8 10M12 14L16 10M5 18H19"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>

                <button
                    type="button"
                    className="result-action-button result-copy-button"
                    onClick={handleCopyImage}
                    disabled={isExporting || !canCopyImage}
                    aria-label={isCopying ? "Copying PNG" : "Copy PNG"}
                    title={
                        canCopyImage
                            ? isCopying
                                ? "Copying PNG"
                                : "Copy PNG"
                            : "Copy image is not supported in this browser"
                    }
                >
                    <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                    >
                        <path
                            d="M9 7H7C5.89543 7 5 7.89543 5 9V19C5 20.1046 5.89543 21 7 21H15C16.1046 21 17 20.1046 17 19V17M9 7C9 5.89543 9.89543 5 11 5H13.5M9 7C9 8.10457 9.89543 9 11 9H13C14.1046 9 15 8.10457 15 7M13.5 5C13.5 3.89543 14.3954 3 15.5 3H17C18.1046 3 19 3.89543 19 5V15C19 16.1046 18.1046 17 17 17H15M13.5 5C14.3807 5.65132 15 6.09798 15 7"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );
}
